import { getDateRange, momentStringFromDateTrunc } from '../../dateUtils';
import { generateTimeBasedComparison, interleaveComparisonPoints } from '../../features/comparison/comparisonEngine';
import {
  DateGranularity,
  EffectiveDefinition,
  DataTable,
  DateTrunc,
  DataRow,
  HistoricalComparisonSpec,
  makeHistoricalComparisonSpec,
} from '../../models';
import { accessorForReference } from '../../models/Accessor';
import { ColumnReference, Calculation } from '../../models/EffectiveDefinition';
import { TransformCreator, TransformFunction } from '../transform';

export const TimeShiftType = 'time-shift';

/**
 * A TimeShift transform
 *
 * This transformation is responsible for our interactive historical comparisons feature.
 *
 * It produces new columns of data, showing the values of the given input columns in the past.
 * Specifically, whatever `amount` of the given `unit` in the past.
 *
 * Currently, most of the actual implementation lives src/features/threshold/comparisonEngine.ts
 * because that's where it was initially implemented. We should move that implementation here in the future.
 *
 * @param source The column to use when calculating the time shift. Must be a `DateTime` column. Currently always `timestamp`.
 * @param granularity The `DateGranularity` to shift
 * @param numberOfUnits The amount of `granularity`s to shift
 * @param copy An array of pairs of `ColumnReference`s to copy `from` and `to.
 */
export interface TimeShiftCalculation {
  type: typeof TimeShiftType;
  source: ColumnReference;
  granularity: DateGranularity;
  numberOfUnits: number;
  copy?: ReadonlyArray<CopyEntry>;
}
export interface CopyEntry {
  readonly from: ColumnReference;
  readonly to: ColumnReference;
}

const isTimeShiftCalculation = (calc?: Calculation): calc is TimeShiftCalculation => {
  return calc !== undefined && calc.type === TimeShiftType;
};

export const timeShiftTransformCreator: TransformCreator = (
  definition: EffectiveDefinition,
  name: string
): TransformFunction => {
  return (sourceTable, windowedTable) => timeShiftTransform(definition, name, sourceTable, windowedTable);
};

export const timeShiftTransform = (
  definition: EffectiveDefinition,
  name: string,
  sourceTable: DataTable,
  windowedTable: DataTable
): DataTable => {
  const calculation = definition.schema[name]?.calculation;
  if (!isTimeShiftCalculation(calculation)) {
    throw new Error(`Could not resolve TimeShift calculation with name: ${name}`);
  }
  const unit = momentStringFromDateTrunc(calculation.granularity as string as DateTrunc);
  const comparison = makeLegacyComparison(calculation);
  const currentRange = getDateRange(windowedTable);
  if (!currentRange || !unit) {
    return windowedTable;
  }

  const timestampAccessor = accessorForReference<Date>(calculation.source);
  const outputFunc = makeOutputFunc(definition, name, calculation);

  const comparisonPoints = generateTimeBasedComparison(
    sourceTable,
    comparison,
    unit,
    currentRange,
    timestampAccessor,
    outputFunc
  );
  return interleaveComparisonPoints(windowedTable, comparisonPoints, timestampAccessor);
};

const makeLegacyComparison = (calculation: TimeShiftCalculation): HistoricalComparisonSpec => {
  const compareTo = calculation.granularity as string as DateTrunc;
  const numberOfPeriods = calculation.numberOfUnits;

  return makeHistoricalComparisonSpec(compareTo, '', numberOfPeriods);
};

export interface CalcWithSourceAndCopy {
  source?: ColumnReference;
  copy?: ReadonlyArray<CopyEntry>;
}

export const makeOutputFunc = (
  definition: EffectiveDefinition,
  calcName: string,
  calculation: CalcWithSourceAndCopy
): ((row: DataRow) => DataRow) => {
  const obj: Record<string, ColumnReference> = {};
  if (definition.schema[calcName] !== undefined && calculation.source) {
    obj[calcName] = calculation.source;
  }
  Object.entries(definition.schema).forEach(([key, columnSchema]) => {
    if (columnSchema.calculation && columnSchema.calculation.name === calcName) {
      obj[key] = columnSchema.calculation.source;
    }
  });

  calculation.copy?.forEach((entry) => {
    obj[entry.to.column] = entry.from;
  });

  return mapperFromColumnRefs(obj);
};

export function mapperFromColumnRefs(obj: Record<string, ColumnReference>): (row: DataRow) => DataRow {
  return (row: DataRow) => {
    const newRow: DataRow = {};
    Object.entries(obj).forEach(([key, value]) => {
      newRow[key] = accessorForReference(value)(row);
    });
    return newRow;
  };
}
